import React from 'react';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  LeftOutlined,
  PhoneOutlined,
  RightOutlined,
  UnorderedListOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import {
  Button,
  Calendar,
  Empty,
  Segmented,
  Skeleton,
  Tag,
  Typography,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  InterviewMeetingType,
  InterviewStatus,
} from '../models/interview.model';
import { CalendarInterview } from '../utils/interview-calendar.utils';

export type CalendarView = 'month' | 'agenda';
export type LoadState = 'loading' | 'ready' | 'error';

type CalendarSummary = {
  total: number;
  confirmed: number;
  proposed: number;
};

const STATUS_LABELS: Record<InterviewStatus, string> = {
  PROPOSED: 'Proposed',
  CONFIRMED: 'Confirmed',
  RESCHEDULED: 'Rescheduled',
  CANCELLED: 'Cancelled',
  COMPLETED: 'Completed',
  NO_SHOW: 'No show',
};

const MEETING_LABELS: Record<InterviewMeetingType, string> = {
  CALL: 'Phone call',
  VIDEO: 'Video call',
  IN_PERSON: 'In person',
};

const getMeetingIcon = (meetingType: InterviewMeetingType) => {
  if (meetingType === 'CALL') {
    return <PhoneOutlined aria-hidden />;
  }
  if (meetingType === 'IN_PERSON') {
    return <EnvironmentOutlined aria-hidden />;
  }
  return <VideoCameraOutlined aria-hidden />;
};

const InterviewEntryDetails: React.FC<{ entry: CalendarInterview }> = ({
  entry,
}) => {
  const { interview } = entry;

  return (
    <>
      <div className="interview-calendar-item__topline">
        <span className="interview-calendar-item__time">
          <ClockCircleOutlined aria-hidden />
          {dayjs(entry.startsAt).format('h:mm A')}
        </span>
        <Tag
          className={`interview-status interview-status--${interview.status.toLowerCase()}`}
        >
          {STATUS_LABELS[interview.status]}
        </Tag>
      </div>
      <strong className="interview-calendar-item__title">
        {interview.title}
      </strong>
      <div className="interview-calendar-item__meta">
        <span>
          {getMeetingIcon(interview.meetingType)}
          {MEETING_LABELS[interview.meetingType]}
        </span>
        <span>{interview.durationMinutes} min</span>
        {entry.isProposedSlot ? <span>Candidate option</span> : null}
      </div>
    </>
  );
};

type ToolbarProps = {
  value: Dayjs;
  itemCount: number;
  calendarView: CalendarView;
  onViewChange: (view: CalendarView) => void;
  onToday: () => void;
  onChangeMonth: (amount: number) => void;
};

export const InterviewCalendarToolbar: React.FC<ToolbarProps> = ({
  value,
  itemCount,
  calendarView,
  onViewChange,
  onToday,
  onChangeMonth,
}) => (
  <header className="interview-calendar-toolbar">
    <div className="interview-calendar-period">
      <div
        className="interview-calendar-navigation"
        aria-label="Interview Agenda navigation"
      >
        <Button onClick={onToday}>Today</Button>
        <Button
          aria-label="Previous month"
          icon={<LeftOutlined />}
          onClick={() => onChangeMonth(-1)}
        />
        <Button
          aria-label="Next month"
          icon={<RightOutlined />}
          onClick={() => onChangeMonth(1)}
        />
      </div>
      <div>
        <Typography.Title id="calendar-period-title" level={3}>
          {value.format('MMMM YYYY')}
        </Typography.Title>
        <Typography.Text type="secondary">
          {itemCount === 0
            ? 'No interviews scheduled this month'
            : `${itemCount} interview${itemCount === 1 ? '' : 's'} this month`}
        </Typography.Text>
      </div>
    </div>

    <Segmented
      aria-label="Interview Agenda view"
      value={calendarView}
      onChange={(nextView) => onViewChange(nextView as CalendarView)}
      options={[
        {
          label: 'Month',
          value: 'month',
          icon: <CalendarOutlined />,
        },
        {
          label: 'Agenda',
          value: 'agenda',
          icon: <UnorderedListOutlined />,
        },
      ]}
    />
  </header>
);

type MonthViewProps = {
  value: Dayjs;
  byDay: Map<string, CalendarInterview[]>;
  selectedItems: CalendarInterview[];
  loadState: LoadState;
  summary: CalendarSummary;
  onSelectDate: (value: Dayjs) => void;
  onOpenApplication: (jobApplicationId: string) => void;
  onReviewApplications: () => void;
};

export const InterviewMonthView: React.FC<MonthViewProps> = ({
  value,
  byDay,
  selectedItems,
  loadState,
  summary,
  onSelectDate,
  onOpenApplication,
  onReviewApplications,
}) => (
  <div
    className="interview-calendar-layout"
    aria-busy={loadState === 'loading'}
  >
    <div className="interview-calendar-main">
      <Calendar
        className="interview-calendar-grid"
        value={value}
        headerRender={() => null}
        onChange={onSelectDate}
        cellRender={(date, info) => {
          if (info.type !== 'date') {
            return info.originNode;
          }

          const dayItems = byDay.get(date.format('YYYY-MM-DD')) || [];
          return (
            <div className="interview-calendar-cell-events">
              {dayItems.slice(0, 2).map((entry) => (
                <span
                  className={`interview-calendar-event interview-calendar-event--${entry.interview.status.toLowerCase()}`}
                  key={`${entry.interview.id}-${entry.startsAt}`}
                >
                  <span>{dayjs(entry.startsAt).format('h:mm')}</span>
                  <strong>{entry.interview.title}</strong>
                </span>
              ))}
              {dayItems.length > 2 ? (
                <span className="interview-calendar-more">
                  +{dayItems.length - 2} more
                </span>
              ) : null}
            </div>
          );
        }}
      />
    </div>

    <aside
      className="interview-calendar-day-panel"
      aria-label="Selected day schedule"
    >
      <div className="interview-calendar-day-panel__heading">
        <span className="interview-calendar-day-panel__date">
          {value.format('DD')}
        </span>
        <div>
          <Typography.Title level={4}>{value.format('dddd')}</Typography.Title>
          <Typography.Text type="secondary">
            {value.format('MMMM D, YYYY')}
          </Typography.Text>
        </div>
      </div>

      <div className="interview-calendar-day-panel__body">
        {loadState === 'loading' ? (
          <Skeleton active paragraph={{ rows: 5 }} title={false} />
        ) : selectedItems.length > 0 ? (
          <div className="interview-calendar-day-list">
            {selectedItems.map((entry) => (
              <button
                className="interview-calendar-item"
                key={`${entry.interview.id}-${entry.startsAt}`}
                onClick={() =>
                  onOpenApplication(entry.interview.jobApplicationId)
                }
                type="button"
              >
                <InterviewEntryDetails entry={entry} />
              </button>
            ))}
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                <strong>No interviews scheduled</strong>
                <small>
                  Choose another date or review job applications.
                </small>
              </span>
            }
          >
            <Button onClick={onReviewApplications}>Review applications</Button>
          </Empty>
        )}
      </div>

      <dl className="interview-calendar-summary">
        <div>
          <dt>Total</dt>
          <dd>{summary.total}</dd>
        </div>
        <div>
          <dt>Confirmed</dt>
          <dd>{summary.confirmed}</dd>
        </div>
        <div>
          <dt>Proposed</dt>
          <dd>{summary.proposed}</dd>
        </div>
      </dl>
    </aside>
  </div>
);

type AgendaViewProps = {
  groups: Array<[string, CalendarInterview[]]>;
  loadState: LoadState;
  onOpenApplication: (jobApplicationId: string) => void;
  onReviewApplications: () => void;
};

export const InterviewAgendaView: React.FC<AgendaViewProps> = ({
  groups,
  loadState,
  onOpenApplication,
  onReviewApplications,
}) => (
  <div className="interview-agenda-view" aria-busy={loadState === 'loading'}>
    {loadState === 'loading' ? (
      <Skeleton active paragraph={{ rows: 9 }} />
    ) : groups.length > 0 ? (
      groups.map(([dateKey, dayEntries]) => (
        <section className="interview-agenda-day" key={dateKey}>
          <header>
            <span>{dayjs(dateKey).format('ddd')}</span>
            <strong>{dayjs(dateKey).format('D')}</strong>
            <div>
              <Typography.Title level={4}>
                {dayjs(dateKey).format('dddd')}
              </Typography.Title>
              <Typography.Text type="secondary">
                {dayjs(dateKey).format('MMMM D, YYYY')}
              </Typography.Text>
            </div>
          </header>
          <div className="interview-agenda-day__items">
            {dayEntries.map((entry) => (
              <button
                className="interview-calendar-item interview-calendar-item--agenda"
                key={`${entry.interview.id}-${entry.startsAt}`}
                onClick={() =>
                  onOpenApplication(entry.interview.jobApplicationId)
                }
                type="button"
              >
                <InterviewEntryDetails entry={entry} />
              </button>
            ))}
          </div>
        </section>
      ))
    ) : (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No interviews scheduled this month"
      >
        <Button type="primary" onClick={onReviewApplications}>
          Review applications
        </Button>
      </Empty>
    )}
  </div>
);
